// Port of SuiteModel/DashStyle.swift (the style itself; the census is in dashes.ts): how a manuscript sets a dash
// between words — closed up (word—word), a spaced em dash (word — word) or a spaced en dash (word – word). A project keeps
// its choice in `settings.dashStyle` (synced); without one, the style its dashes mostly use.
import type { ProjectRecord } from '../model/documents.ts';

export type DashStyle = 'closed' | 'spaced-em' | 'spaced-en';

/** `DashStyle.allCases`, in order (ties in `usual` go to the earlier). */
export const DASH_STYLES: readonly DashStyle[] = ['closed', 'spaced-em', 'spaced-en'];

export const dashStyleName = (s: DashStyle) => (s === 'closed' ? 'Closed up em dash' : s === 'spaced-em' ? 'Spaced em dash' : 'Spaced en dash');
export const dashStyleExample = (s: DashStyle) => (s === 'closed' ? 'word—word' : s === 'spaced-em' ? 'word — word' : 'word – word');
export const dashOf = (s: DashStyle) => (s === 'spaced-en' ? '–' : '—');
export const isSpaced = (s: DashStyle) => s !== 'closed';

export type DashCensus = Partial<Record<DashStyle, number>>;

/** `DashStyle.usual(census:)`: the style with the most dashes; on a tie (or none), the first in `allCases`. */
export function usualFromCensus(counts: DashCensus): DashStyle {
  let best: DashStyle = 'closed';
  for (const style of DASH_STYLES) if ((counts[style] ?? 0) > (counts[best] ?? 0)) best = style;
  return best;
}

/** `ProjectDocument.dashStyle()`: the project's chosen style (null: follow the manuscript's usual one). */
export function projectDashStyle(project: ProjectRecord): DashStyle | null {
  const raw = project.settings.dashStyle;
  return typeof raw === 'string' && (DASH_STYLES as readonly string[]).includes(raw) ? (raw as DashStyle) : null;
}
